import React from 'react';

import {
  ActionIcon,
  Paper,
  Select,
  Space,
  TextInput,
  Title,
  Tooltip,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { IconFilterOff, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import ButtonMenu from 'components/ButtonMenu';
import { Document } from 'types';

import { DocumentTemplateSearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `'title search type clear button'`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title button button'
        'search type type clear'
      `,
    },
    // [theme.fn.smallerThan('md')]: {
    //   gridTemplateColumns: 'max-content auto auto min-content',
    //   gridTemplateRows: 'auto auto auto auto',
    //   gridTemplateAreas: `
    //     'title title title title'
    //     'search search search search'
    //     'type type status status'
    //     'lastName . . clear'
    //   `,
    // },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto min-content min-content',
      gridTemplateRows: 'auto auto auto auto',
      gridTemplateAreas: `
        'title button button'
        'search search search'
        'type type type'
        '. . clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  search: {
    gridArea: 'search',
  },
  type: {
    gridArea: 'type',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
}));

const DocumentTemplatesPageHeader = ({
  searchFilters,
  initialFilterValues,
  setSearchFilters,
  openWaiverForm,
  openContractForm,
  permission = true,
}: {
  searchFilters: DocumentTemplateSearchFilters;
  initialFilterValues: DocumentTemplateSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<DocumentTemplateSearchFilters>>;
  openWaiverForm: () => void;
  openContractForm: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const handleTypeChange = (value: Document['type'] | null): void => {
    setSearchFilters(prev => ({ ...prev, type: value }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prev => ({ ...prev, search: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialFilterValues);
  };

  const handleClearSearchFilter = (): void => {
    setSearchFilters(prev => ({ ...prev, search: '' }));
  };

  return (
    <Paper className={classes.header}>
      <Title order={1} size='h1' pr='xl' className={classes.title}>
        Document Templates
      </Title>
      <TextInput
        className={classes.search}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search description'
        onChange={handleSearchChange}
        value={searchFilters.search}
        data-cy='searchDocumentTemplate'
        rightSection={
          !!searchFilters.search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Select
        className={classes.type}
        placeholder='Select type'
        data-cy='selectTypeTemplateDocument'
        data={[
          { label: 'Contract', value: 'contract', 'data-cy': 'contractOption' },
          { label: 'Waiver', value: 'waiver', 'data-cy': 'waiverOption' },
        ]}
        value={searchFilters.type}
        onChange={handleTypeChange}
        clearable
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters} data-cy='filterTemplateButton'>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>
      <ButtonMenu
        data-cy='addTemplateButton'
        className={classes.button}
        title='Template'
        disabled={!permission}
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        items={[
          {
            label: 'Contract',
            onClick: () => openContractForm(),
            'data-cy': 'createContractButton',
          },
          {
            label: 'Waiver',
            onClick: () => openWaiverForm(),
            'data-cy': 'createWaiverButton',
          },
        ]}
      />
    </Paper>
  );
};

export default DocumentTemplatesPageHeader;
